import { CheckIcon } from 'lucide-react'
import { useEnumsQuery } from '@/api/accommodation/hooks'
import { trackSearchCategoryFilterApplied } from '@/lib/track'
import { cn } from '@/lib/utils'
import { categoryIcon, parseCategorySlugs, serializeCategorySlugs } from '../utils/categoryOptions'
import { useSearchFilters } from './useSearchFilters'

function useCategorySelection(commit: boolean) {
  const { localFilters, updateLocalFilters } = useSearchFilters()
  const { data: enums } = useEnumsQuery()
  // Admin order; a category without a slug can't go into the URL.
  const categories = (enums?.locationCategories ?? []).flatMap((category) =>
    category.slug ? [{ ...category, slug: category.slug }] : [],
  )
  const selectedSlugs = parseCategorySlugs(localFilters.category)

  const toggle = (slug: string) => {
    const next = selectedSlugs.includes(slug)
      ? selectedSlugs.filter((selected) => selected !== slug)
      : [...selectedSlugs, slug]
    updateLocalFilters({ category: serializeCategorySlugs(next) }, commit)
    trackSearchCategoryFilterApplied(next)
  }

  return { categories, selectedSlugs, toggle }
}

export function CategoryFilterDesktop() {
  const { categories, selectedSlugs, toggle } = useCategorySelection(true)

  if (categories.length === 0) {
    return null
  }

  return (
    <div role="group" aria-label="Typ ubytování" className="flex flex-wrap gap-2">
      {categories.map((category) => {
        const selected = selectedSlugs.includes(category.slug)
        const Icon = categoryIcon(category.slug)
        return (
          <button
            key={category.id}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(category.slug)}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
              selected
                ? 'border-red text-red bg-white'
                : 'border-transparent bg-slate-100 text-slate-700 hover:bg-slate-200',
            )}
          >
            <Icon className="size-4" />
            {category.name}
            {selected && <CheckIcon className="size-4" />}
          </button>
        )
      })}
    </div>
  )
}

export function CategoryFilterMobile() {
  const { categories, selectedSlugs, toggle } = useCategorySelection(false)

  if (categories.length === 0) {
    return null
  }

  return (
    <div role="group" aria-label="Typ ubytování" className="grid grid-cols-4 gap-2">
      {categories.map((category) => {
        const selected = selectedSlugs.includes(category.slug)
        const Icon = categoryIcon(category.slug)
        return (
          <button
            key={category.id}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(category.slug)}
            className={cn(
              'relative flex cursor-pointer flex-col items-center gap-1 rounded-xl border px-1 py-3 text-xs transition-colors outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
              selected ? 'border-red text-red bg-red-50' : 'border-transparent bg-slate-100 text-slate-600',
            )}
          >
            {selected && (
              <span className="bg-red absolute top-1 right-1 flex size-4 items-center justify-center rounded-full text-white">
                <CheckIcon className="size-3" strokeWidth={3} />
              </span>
            )}
            <Icon className="size-6" strokeWidth={1.5} />
            <span className="w-full truncate text-center">{category.name}</span>
          </button>
        )
      })}
    </div>
  )
}
